import React, { useState } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import {
  FaUser,
  FaPhoneAlt,
  FaDoorOpen,
  FaCalendarAlt,
  FaHeartbeat,
  FaStethoscope,
  FaTasks,
  FaClock,
  FaTemperatureHigh,
  FaWeight,
  FaRulerVertical,
} from "react-icons/fa";
import MedicationTracker from "../components/MedicationTracker";
import DoctorNotes from "../components/DoctorNotes";
import VitalsLogger from "../components/VitalsLogger";
import TaskList from "../components/TaskList";

const TreatmentTracker = () => {
  const [activeTreatment, setActiveTreatment] = useState(0);

  const patientInfo = {
    name: "John Doe",
    age: 45,
    gender: "Male",
    contact: "+1 234-567-8901",
    room: "205-B",
    admissionDate: "2023-10-01",
  };

  const vitals = {
    lastUpdated: "2024-11-30 13:26:56",
    readings: [
      { type: "SpO₂", value: "34%", icon: FaHeartbeat },
      { type: "Blood Pressure", value: "34/3434 mmHg", icon: FaHeartbeat },
      { type: "Blood Sugar", value: "433 mg/dL", icon: FaHeartbeat },
      { type: "Pulse", value: "434 bpm", icon: FaHeartbeat },
      { type: "Temperature", value: "434°C", icon: FaTemperatureHigh },
      { type: "Weight", value: "34 kg", icon: FaWeight },
      { type: "Height", value: "34 cm", icon: FaRulerVertical },
    ],
  };

  const treatments = [
    {
      id: 1,
      name: "Chemotherapy",
      doctor: "Dr. Smith",
      progress: 70,
      startDate: "2023-11-01",
      endDate: "2023-12-15",
      procedures: ["IV Therapy", "Blood Tests", "Oncology Assessment"],
      medications: [],
      notes: [],
      tasks: [],
    },
    {
      id: 2,
      name: "Physiotherapy",
      doctor: "Dr. Johnson",
      progress: 40,
      startDate: "2023-10-15",
      endDate: "2023-12-01",
      procedures: ["Range of Motion Exercises", "Strength Training"],
      medications: [],
      notes: [],
      tasks: [],
    },
    {
      id: 3,
      name: "Cardiology",
      doctor: "Dr. Gupta",
      progress: 50,
      startDate: "2023-11-10",
      endDate: "2023-12-20",
      procedures: ["Echocardiogram", "ECG Monitoring", "Medication Review"],
      medications: [],
      notes: [],
      tasks: [],
    },
  ];

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Title */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800">Treatment Tracker</h1>
        <p className="text-gray-500 mt-2">
          Monitor and manage all treatments for the patient
        </p>
      </div>

      {/* Patient Info */}
      <div className="p-6 bg-white shadow-lg rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Patient Details */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <FaUser className="text-blue-500" />
              <span>Patient Details</span>
            </h2>
            <p className="flex items-center space-x-2 text-gray-600 mt-2">
              <FaStethoscope className="text-gray-500" />
              <span>
                <strong>Name:</strong> {patientInfo.name}
              </span>
            </p>
            <p className="flex items-center space-x-2 text-gray-600">
              <FaStethoscope className="text-gray-500" />
              <span>
                <strong>Age:</strong> {patientInfo.age}
              </span>
            </p>
            <p className="flex items-center space-x-2 text-gray-600">
              <FaStethoscope className="text-gray-500" />
              <span>
                <strong>Gender:</strong> {patientInfo.gender}
              </span>
            </p>
          </div>

          {/* Contact Info */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <FaPhoneAlt className="text-green-500" />
              <span>Contact Info</span>
            </h2>
            <p className="flex items-center space-x-2 text-gray-600 mt-2">
              <FaPhoneAlt className="text-gray-500" />
              <span>
                <strong>Phone:</strong> {patientInfo.contact}
              </span>
            </p>
            <p className="flex items-center space-x-2 text-gray-600">
              <FaDoorOpen className="text-gray-500" />
              <span>
                <strong>Room No:</strong> {patientInfo.room}
              </span>
            </p>
            <p className="flex items-center space-x-2 text-gray-600">
              <FaCalendarAlt className="text-gray-500" />
              <span>
                <strong>Admission Date:</strong> {patientInfo.admissionDate}
              </span>
            </p>
          </div>

          {/* Vitals Section */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <FaHeartbeat className="text-red-500" />
              <span>Last Updated Vitals</span>
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              <strong>Last Updated:</strong> {vitals.lastUpdated}
            </p>
            <div className="mt-4 space-y-2">
              {vitals.readings.map((vital, index) => (
                <p
                  key={index}
                  className="flex items-center space-x-2 text-gray-600"
                >
                  <vital.icon className="text-blue-500" />
                  <span>
                    <strong>{vital.type}:</strong> {vital.value}
                  </span>
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>

      <VitalsLogger />

      {/* Treatments Tabs */}
      <div className="mb-6">
        <div className="flex space-x-4 border-b pb-2">
          {treatments.map((treatment, index) => (
            <button
              key={treatment.id}
              className={`px-4 py-2 text-lg font-semibold rounded-t-lg focus:outline-none ${
                activeTreatment === index
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
              onClick={() => setActiveTreatment(index)}
            >
              {treatment.name}
            </button>
          ))}
        </div>
      </div>

      {/* Active Treatment Details */}
      <div className="p-6 bg-white shadow-lg rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 flex items-center space-x-2">
              <FaStethoscope className="text-blue-500" />
              <span>{treatments[activeTreatment].name}</span>
            </h2>
            <p className="text-gray-600 flex items-center space-x-2 mt-2">
              <FaUser className="text-green-500" />
              <span>
                <strong>Doctor:</strong> {treatments[activeTreatment].doctor}
              </span>
            </p>
            <p className="text-gray-600 flex items-center space-x-2 mt-2">
              <FaCalendarAlt className="text-yellow-500" />
              <span>
                <strong>Start Date:</strong>{" "}
                {new Date(
                  treatments[activeTreatment].startDate
                ).toLocaleDateString()}
              </span>
            </p>
            <p className="text-gray-600 flex items-center space-x-2 mt-2">
              <FaCalendarAlt className="text-orange-500" />
              <span>
                <strong>End Date:</strong>{" "}
                {new Date(
                  treatments[activeTreatment].endDate
                ).toLocaleDateString()}
              </span>
            </p>
            <p className="text-gray-600 flex items-center space-x-2 mt-2">
              <FaClock className="text-purple-500" />
              <span>
                <strong>Days Remaining:</strong>{" "}
                {Math.max(
                  0,
                  Math.ceil(
                    (new Date(treatments[activeTreatment].endDate) -
                      new Date()) /
                      (1000 * 60 * 60 * 24)
                  )
                )}
              </span>
            </p>
            <p className="text-gray-600 flex items-center space-x-2 mt-2">
              <FaTasks className="text-red-500" />
              <span>
                <strong>Procedures:</strong>{" "}
                {treatments[activeTreatment].procedures.join(", ")}
              </span>
            </p>
          </div>

          <div className="w-20">
            <CircularProgressbar
              value={treatments[activeTreatment].progress}
              text={`${treatments[activeTreatment].progress}%`}
              styles={buildStyles({
                pathColor: "#4caf50",
                textColor: "#4caf50",
                trailColor: "#e0e0e0",
              })}
            />
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          <MedicationTracker
            medications={treatments[activeTreatment].medications}
          />
          <DoctorNotes notes={treatments[activeTreatment].notes} />
          <TaskList tasks={treatments[activeTreatment].tasks} />
        </div>
      </div>
    </div>
  );
};

export default TreatmentTracker;
